<script setup lang="ts">
import { computed } from 'vue'

import type { CreatorExtraInfo } from '@/lib/types/creator-detail.types'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  extraInfo: CreatorExtraInfo | null
}>()

const SSN_COUNTRIES = ['SE', 'NO', 'NL']

const businessTypeLabel = computed(() => {
  if (!props.extraInfo) {
    return '-'
  }
  return props.extraInfo.businessType === 'COMPANY' ? 'Company' : 'Private person'
})

const ssnLabel = computed(() => {
  const country = props.extraInfo?.country
  if (country === 'NL') {
    return 'BSN number'
  }
  return 'Social security number'
})

const showSsn = computed(() => {
  if (!props.extraInfo?.country) {
    return false
  }
  return props.extraInfo.businessType === 'INDIVIDUAL' && SSN_COUNTRIES.includes(props.extraInfo.country)
})

const showCompany = computed(() => props.extraInfo?.businessType === 'COMPANY')

const orgIdLabel = computed(() => {
  const country = props.extraInfo?.country
  if (country === 'PL') {
    return 'KRS or REGON'
  }
  if (country === 'DE') {
    return 'Handelsregister-Nummer'
  }
  if (country === 'NL') {
    return 'KVK-nummer'
  }
  return 'Organisation number'
})

const showVat = computed(() => {
  if (!props.extraInfo) {
    return false
  }
  if (props.extraInfo.businessType === 'COMPANY') {
    return props.extraInfo.country !== 'UA'
  }
  return props.extraInfo.country === 'IT' || props.extraInfo.country === 'DE'
})

const vatLabel = computed(() => {
  const country = props.extraInfo?.country
  if (props.extraInfo?.businessType === 'INDIVIDUAL') {
    return country === 'IT' ? 'Tax code' : 'VAT number'
  }
  if (country === 'PL') {
    return 'NIP number'
  }
  if (country === 'NO') {
    return 'MVA number'
  }
  return 'VAT number'
})

const vatValue = computed(() => {
  if (!props.extraInfo) {
    return '-'
  }
  return props.extraInfo.businessType === 'COMPANY' ? props.extraInfo.company.vat || '-' : props.extraInfo.vatNo || '-'
})
</script>
<template>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Tax country</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ extraInfo?.country || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">City</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ extraInfo?.city || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Address</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ extraInfo?.address || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Postal code</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ extraInfo?.postcode || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Legal entity</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ businessTypeLabel }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent v-if="showSsn" align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">{{ ssnLabel }}</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ extraInfo?.ssn || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <template v-if="showCompany">
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">Company name</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.company.name || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">{{ orgIdLabel }}</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.company.organisationId || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
  </template>
  <FlexComponent v-if="showVat" align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">{{ vatLabel }}</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ vatValue }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
</template>
<style scoped lang="scss">
.info-field {
  padding: 16px 32px;
  border-bottom: 1px solid var(--secondary-4);
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
}
.info-field__content {
  flex: 1;
  min-width: 0;
}
</style>
